import React, {useState, useEffect} from 'react';
import {Row, Col, Card, Button, Badge, Spinner, Alert, ProgressBar, Table, Tabs, Tab} from 'react-bootstrap';
import {Line, Bar} from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import {websiteService} from '../api/services/websites';
import {useToastStore} from '../components/UI/Toast';
import {reportService} from "../api/services/reportService";

const WebsitePerformance = ({website, results}) => {
    const [activeHistoryTab, setActiveHistoryTab] = useState('30days');
    const [loading, setLoading] = useState(false);
    const [scanInProgress, setScanInProgress] = useState(false);
    const addToast = useToastStore(state => state.addToast);
    const [ttfbHistoryData, setTtfbHistoryData] = useState(null);

    useEffect(()=>{
        const fetchUptime = async () => {
            return await reportService.getMonitoringResults(website.id, "uptime", 2*24*60 / 5);

        }
        const result = fetchUptime();
        result.then((dataPerformance) => {
            const ttfbValues = [];
            const dates = [];

            console.log(dataPerformance)
            dataPerformance?.data?.forEach(item => {
                console.log(item);
                if (item && item.details?.response_time !== undefined) {
                    if (item.details.response_time < 0) {
                        return;
                    }
                    ttfbValues.push(item.details.response_time);
                    const date = new Date(item.createdAt);
                    dates.push(date.toLocaleDateString()+" "+ date.toLocaleTimeString([],{hour: '2-digit', minute:'2-digit'}));
                }
            });
            if (dates.length > 0 && ttfbValues.length > 0) {
                setTtfbHistoryData({
                    labels: dates,
                    datasets: [
                        {
                            label: 'TTFB (ms)',
                            data: ttfbValues,
                            fill: false,
                            backgroundColor: 'rgba(75, 192, 192, 0.2)',
                            borderColor: 'rgba(75, 192, 192, 1)',
                            tension: 0.4
                        }
                    ]
                });
            }
        });
    }, [website]);

    // Zkontrolujeme, zda máme validní data pro zobrazení
    const hasValidData = results && Array.isArray(results) && results.length > 0 &&
        results[0].details &&
        results[0].details.metrics &&
        typeof results[0].details.metrics === 'object';

    // Funkce pro spuštění kontroly výkonu
    const handleRunPerformanceCheck = async (websiteId) => {
        setScanInProgress(true);
        try {
            await websiteService.runPerformanceCheck(websiteId);
            addToast('Kontrola výkonu byla úspěšně spuštěna. Výsledky budou brzy k dispozici.', 'success');
        } catch (error) {
            addToast('Nepodařilo se spustit kontrolu výkonu.', 'danger');
        } finally {
            setScanInProgress(false);
        }
    };

    // Pokud nemáme validní data, zobrazíme možnost spustit kontrolu
    if (!hasValidData) {
        return (
            <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                Pro tento web zatím nejsou k dispozici data o výkonu. Spusťte kontrolu výkonu.
                <div className="mt-3">
                    <Button
                        variant="primary"
                        disabled={scanInProgress}
                        onClick={() => handleRunPerformanceCheck(website.id)}
                    >
                        {scanInProgress ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"
                                         className="me-2"/>
                                Probíhá kontrola...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-tachometer-alt me-2"></i> Spustit kontrolu výkonu
                            </>
                        )}
                    </Button>
                </div>
            </Alert>
        );
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání výsledků výkonu...</span>
                </Spinner>
            </div>
        );
    }

    // Bezpečně získat metriky z výsledků (použijeme první výsledek v poli)
    const metrics = results[0].details.metrics;

    // Funkce pro určení barvy/variant podle hodnoty
    const getPerformanceColor = (value, type) => {
        switch (type) {
            case 'ttfb':
                // Ošetření negativních hodnot TTFB
                if (value < 0) return 'warning';
                return value < 300 ? 'success' : value < 1000 ? 'warning' : 'danger';
            case 'fcp':
                return value < 1800 ? 'success' : value < 3000 ? 'warning' : 'danger';
            case 'lcp':
                return value < 2500 ? 'success' : value < 4000 ? 'warning' : 'danger';
            case 'cls':
                return value < 0.1 ? 'success' : value < 0.25 ? 'warning' : 'danger';
            case 'score':
                return value >= 90 ? 'success' : value >= 50 ? 'warning' : 'danger';
            default:
                return 'primary';
        }
    };

    // Funkce pro získání textového hodnocení
    const getPerformanceRating = (value, type) => {
        switch (type) {
            case 'ttfb':
                if (value < 0) return 'Chybná hodnota';
                return value < 300 ? 'Výborné' : value < 1000 ? 'Dobré' : 'Pomalé';
            case 'fcp':
                return value < 1800 ? 'Výborné' : value < 3000 ? 'Dobré' : 'Pomalé';
            case 'lcp':
                return value < 2500 ? 'Výborné' : value < 4000 ? 'Dobré' : 'Pomalé';
            case 'cls':
                return value < 0.1 ? 'Výborné' : value < 0.25 ? 'Dobré' : 'Špatné';
            case 'score':
                return value >= 90 ? 'Výborné' : value >= 50 ? 'Dobré' : 'Slabé';
            default:
                return 'Neznámé';
        }
    };

    // Graf performance dat
    const performanceData = {
        labels: ['TTFB', 'FCP', 'LCP', 'CLS'],
        datasets: [
            {
                label: 'Aktuální hodnoty',
                data: [
                    metrics.ttfb < 0 ? 0 : metrics.ttfb,
                    metrics.fcp,
                    metrics.lcp,
                    metrics.cls * 100, // Převod CLS na škálu vhodnější pro zobrazení
                ],
                backgroundColor: [
                    `rgba(${getPerformanceColor(metrics.ttfb, 'ttfb') === 'success' ? '40, 167, 69' :
                        getPerformanceColor(metrics.ttfb, 'ttfb') === 'warning' ? '255, 193, 7' : '220, 53, 69'}, 0.8)`,
                    `rgba(${getPerformanceColor(metrics.fcp, 'fcp') === 'success' ? '40, 167, 69' :
                        getPerformanceColor(metrics.fcp, 'fcp') === 'warning' ? '255, 193, 7' : '220, 53, 69'}, 0.8)`,
                    `rgba(${getPerformanceColor(metrics.lcp, 'lcp') === 'success' ? '40, 167, 69' :
                        getPerformanceColor(metrics.lcp, 'lcp') === 'warning' ? '255, 193, 7' : '220, 53, 69'}, 0.8)`,
                    `rgba(${getPerformanceColor(metrics.cls, 'cls') === 'success' ? '40, 167, 69' :
                        getPerformanceColor(metrics.cls, 'cls') === 'warning' ? '255, 193, 7' : '220, 53, 69'}, 0.8)`,
                ],
                borderWidth: 1,
            },
        ],
    };

    // Konfigurace grafu
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const index = context.dataIndex;
                        const value = context.dataset.data[index];

                        if (index === 3) { // CLS
                            return `CLS: ${(value / 100).toFixed(2)}`;
                        }

                        const metrics = ['TTFB', 'FCP', 'LCP'];
                        return `${metrics[index]}: ${value} ms`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Hodnota (ms / CLS * 100)'
                }
            }
        }
    };

    // Generování historických dat
    const generateHistoricalData = (results) => {
        // Použijeme reálná data z výsledků
        const labels = results.map(result =>
            new Date(result.createdAt).toLocaleDateString()
        );
        const scoreData = results.map(result =>
            result.details.metrics?.performance_score
        );

        return {
            labels,
            scores: scoreData
        };
    };

    // Generování historických dat podle vybraného období
    const historicalData = generateHistoricalData(
        results.slice(0, activeHistoryTab === '7days' ? 7 :
            activeHistoryTab === '30days' ? 30 : 90)
    );

    // Data pro historický graf skóre
    const scoreHistoryData = {
        labels: historicalData.labels,
        datasets: [
            {
                label: 'Performance skóre',
                data: historicalData.scores,
                borderColor: 'rgba(54, 162, 235, 1)',
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                tension: 0.4,
                fill: true,
            },
        ],
    };

    // Konfigurace pro historický graf
    const historyChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: false,
                min: Math.max(0, Math.min(...historicalData.scores) - 10),
                max: 100
            }
        }
    };

    // Konfigurace pro graf TTFB
    const ttfbHistoryChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Historie TTFB (Time to First Byte)'
            }
        },
        scales: {
            y: {
                title: {
                    display: true,
                    text: 'TTFB (ms)'
                },
                suggestedMin: 0
            },
            x: {
                title: {
                    display: true,
                    text: 'Datum'
                }
            }
        }
    };


    // Vytvoření seznamu problémů výkonu a doporučení
    const performanceIssues = [];

    if (metrics.ttfb < 0) {
        performanceIssues.push({
            name: 'Time to First Byte (TTFB)',
            value: `${metrics.ttfb.toFixed(2)} ms`,
            severity: 'warning',
            recommendation: 'Zkontrolujte měření TTFB, hodnota je neplatná.'
        });
    } else if (metrics.ttfb >= 300) {
        performanceIssues.push({
            name: 'Time to First Byte (TTFB)',
            value: `${metrics.ttfb.toFixed(2)} ms`,
            severity: getPerformanceColor(metrics.ttfb, 'ttfb'),
            recommendation: 'Zvažte rychlejší hosting nebo využití cache.'
        });
    }

    if (metrics.fcp >= 1800) {
        performanceIssues.push({
            name: 'First Contentful Paint (FCP)',
            value: `${metrics.fcp.toFixed(2)} ms`,
            severity: getPerformanceColor(metrics.fcp, 'fcp'),
            recommendation: 'Optimalizujte nahrávání CSS a JavaScript.'
        });
    }

    if (metrics.lcp >= 2500) {
        performanceIssues.push({
            name: 'Largest Contentful Paint (LCP)',
            value: `${metrics.lcp.toFixed(2)} ms`,
            severity: getPerformanceColor(metrics.lcp, 'lcp'),
            recommendation: 'Optimalizujte obrázky a zrychlete načítání hlavního obsahu.'
        });
    }

    if (metrics.cls >= 0.1) {
        performanceIssues.push({
            name: 'Cumulative Layout Shift (CLS)',
            value: metrics.cls.toFixed(2),
            severity: getPerformanceColor(metrics.cls, 'cls'),
            recommendation: 'Definujte rozměry obrázků a zabraňte posouvání obsahu.'
        });
    }

    // Přidáme problémy z výsledků
    if (results[0].details.issues && results[0].details.issues.length > 0) {
        results[0].details.issues.forEach(issue => {
            if (!performanceIssues.some(pi => pi.name.includes(issue))) {
                performanceIssues.push({
                    name: issue,
                    value: '',
                    severity: 'warning',
                    recommendation: 'Viz doporučené optimalizace níže.'
                });
            }
        });
    }

    return (
        <div className="website-performance fade-in">
            {/* Hlavní karta s celkovým skóre */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="p-4">
                    <Row>
                        <Col md={6} className="mb-3 mb-md-0">
                            <div className="d-flex flex-column align-items-center text-center">
                                <h5 className="mb-3">Performance skóre</h5>
                                <div className="position-relative">
                                    <div
                                        className={`score-circle border-${getPerformanceColor(metrics.performance_score, 'score')}`}>
                                        <div className="score-value">{metrics.performance_score}</div>
                                        <div className="score-max">/100</div>
                                    </div>
                                    <Badge
                                        bg={getPerformanceColor(metrics.performance_score, 'score')}
                                        className="position-absolute top-100 start-50 translate-middle px-3 py-2"
                                    >
                                        {getPerformanceRating(metrics.performance_score, 'score')}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-end">
                                <Button
                                    variant="primary"
                                    onClick={() => handleRunPerformanceCheck(website.id)}
                                    disabled={scanInProgress}
                                >
                                    {scanInProgress ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status"
                                                     aria-hidden="true" className="me-2"/>
                                            Kontroluji...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-sync-alt me-2"></i> Obnovit data
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Graf porovnání metrik */}
            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Porovnání TTFB v čase</h5>
                </Card.Header>
                <Card.Body className="p-4">
                    {ttfbHistoryData && ttfbHistoryData.labels.length > 0 ? (
                    <div style={{height: '300px'}}>
                        <Line data={ttfbHistoryData} options={ttfbHistoryChartOptions}/>
                    </div>
                        ) : (
                        <div className="text-center py-5">
                            <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                            <p>Pro tento web nejsou k dispozici data historie TTFB</p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Graf porovnání metrik */}
            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Porovnání výkonnostních metrik</h5>
                </Card.Header>
                <Card.Body className="p-4">
                    <div style={{height: '300px'}}>
                        <Bar data={performanceData} options={chartOptions}/>
                    </div>
                </Card.Body>
            </Card>

            {/* Historické grafy */}
            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Vývoj výkonu v čase</h5>
                    <div className="btn-group">
                        <Button
                            variant={activeHistoryTab === '7days' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setActiveHistoryTab('7days')}
                        >
                            7 dní
                        </Button>
                        <Button
                            variant={activeHistoryTab === '30days' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setActiveHistoryTab('30days')}
                        >
                            30 dní
                        </Button>
                        <Button
                            variant={activeHistoryTab === '90days' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setActiveHistoryTab('90days')}
                        >
                            90 dní
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Tabs
                        defaultActiveKey="score"
                        className="mb-3"
                    >
                        <Tab eventKey="score" title="Performance skóre">
                            <div style={{height: '300px'}}>
                                <Line data={scoreHistoryData} options={historyChartOptions}/>
                            </div>
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>

            {/* Problémy a doporučení */}
            {performanceIssues.length > 0 && (
                <Card className="shadow-sm">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Nalezené problémy výkonu</h5>
                        <Badge bg="warning" text="dark">{performanceIssues.length}</Badge>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="list-group list-group-flush">
                            {performanceIssues.map((issue, index) => (
                                <div className="list-group-item" key={index}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">{issue.name}</h6>
                                        {issue.value && <Badge bg={issue.severity}>{issue.value}</Badge>}
                                    </div>
                                    <p className="mb-0">
                                        <i className="fas fa-info-circle me-2 text-primary"></i>
                                        {issue.recommendation}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default WebsitePerformance;